/**
 * CMS: News, Guides (both with a cover image), FAQ, Legal pages, Social links
 * and the marketing site's footer (columns + their links). Each resource uses
 * the same list → editor-modal → save/delete loop; only the field definitions
 * differ, so there is one generic CRUD section instead of seven.
 *
 * Field keys are the API's own names (`content`, `sortOrder`) — the panel used
 * to send `body`/`sort`, which the DTOs dropped, so saving never worked.
 */
import { el, clear, toast } from '../../dom';
import {
  adminApi,
  type AdminFaq, type AdminFooterLink, type AdminFooterSection, type AdminGuide,
  type AdminLegal, type AdminNews, type AdminPaymentMethod, type AdminSocial,
} from '../../game-api';
import { fmtDate } from './format';
import { SOCIAL_ICONS, resolveSocialIcon, socialMaskStyle } from '../../ui/social-icons';
import {
  apiMessage, btn, checkbox, confirmDialog, emptyBox, errorBox, input, labelled, modal,
  panel, pill, spinner, table, textarea, toastError,
} from './ui';

type Field =
  | { key: string; label: string; kind: 'text' | 'number' | 'url' }
  /** A calendar day (`YYYY-MM-DD`). Optional by definition — never "required". */
  | { key: string; label: string; kind: 'date'; hint?: string }
  | { key: string; label: string; kind: 'textarea'; rows?: number }
  | { key: string; label: string; kind: 'bool' }
  | { key: string; label: string; kind: 'image' }
  /** Options are fetched when the editor opens — a picker over another resource. */
  | { key: string; label: string; kind: 'select'; options: () => Promise<Opt[]> }
  /** A grid of the shipped social marks — click one to set the field. */
  | { key: string; label: string; kind: 'social-icon' };

interface Opt { value: string; label: string }

interface Resource<T extends { id: string }> {
  title: string;
  fields: Field[];
  columns: string[];
  row: (item: T) => (Node | string)[];
  api: {
    list: () => Promise<T[]>;
    create: (b: Partial<T>) => Promise<T>;
    update: (id: string, b: Partial<T>) => Promise<T>;
    remove: (id: string) => Promise<unknown>;
  };
  empty: string;
  /** Seeded into a NEW entry's editor — mostly so `active` starts ticked. */
  defaults?: Record<string, unknown>;
}

const NEWS: Resource<AdminNews> = {
  title: 'News',
  empty: 'No news posts yet.',
  fields: [
    { key: 'title', label: 'Title', kind: 'text' },
    // The site's news cards show the summary and the tag, so both are editable.
    { key: 'summary', label: 'Summary (shown on the card)', kind: 'textarea', rows: 2 },
    { key: 'content', label: 'Body', kind: 'textarea', rows: 8 },
    { key: 'tag', label: 'Tag', kind: 'text' },
    { key: 'imageUrl', label: 'Cover image', kind: 'image' },
    { key: 'published', label: 'Published', kind: 'bool' },
    {
      key: 'publishedAt', label: 'Publish date', kind: 'date',
      hint: 'Shown on the site and used to order the feed. Leave empty to stamp the moment you publish.',
    },
  ],
  columns: ['Title', 'Image', 'Published', 'Date'],
  row: (n) => [
    n.title,
    n.imageUrl
      ? el('img', 'h-8 w-12 object-cover rounded border border-border', { src: n.imageUrl, alt: '' })
      : el('span', 'text-text-dim text-xs', {}, '—'),
    n.published ? pill('live', 'ok') : pill('draft', 'warn'),
    fmtDate(n.publishedAt ?? n.createdAt),
  ],
  api: adminApi.news as Resource<AdminNews>['api'],
};

const FAQ: Resource<AdminFaq> = {
  title: 'FAQ',
  empty: 'No FAQ entries yet.',
  fields: [
    { key: 'question', label: 'Question', kind: 'text' },
    { key: 'answer', label: 'Answer', kind: 'textarea', rows: 6 },
    { key: 'sortOrder', label: 'Sort order', kind: 'number' },
  ],
  columns: ['Question', 'Sort'],
  row: (f) => [f.question, String(f.sortOrder ?? 0)],
  api: adminApi.faq as Resource<AdminFaq>['api'],
};

const LEGAL: Resource<AdminLegal> = {
  title: 'Legal pages',
  empty: 'No legal pages yet.',
  fields: [
    { key: 'slug', label: 'Slug', kind: 'text' },
    { key: 'title', label: 'Title', kind: 'text' },
    { key: 'content', label: 'Body', kind: 'textarea', rows: 10 },
    {
      key: 'effectiveDate', label: 'Last updated (shown on the page)', kind: 'date',
      hint: 'The date the document CLAIMS. Leave empty to show the date of the last save '
        + '— but a typo fix then restates it, which is why this is editable.',
    },
    { key: 'active', label: 'Published (visible on the site)', kind: 'bool' },
  ],
  columns: ['Slug', 'Title', 'Last updated', 'Published'],
  row: (l) => [
    el('span', 'font-mono text-xs', {}, l.slug),
    l.title,
    fmtDate(l.effectiveDate ?? l.updatedAt),
    l.active ? pill('live', 'ok') : pill('draft', 'warn'),
  ],
  defaults: { active: true },
  api: adminApi.legal as Resource<AdminLegal>['api'],
};

const SOCIAL: Resource<AdminSocial> = {
  title: 'Social links',
  empty: 'No social links yet.',
  fields: [
    { key: 'platform', label: 'Platform', kind: 'text' },
    { key: 'url', label: 'URL', kind: 'url' },
    { key: 'icon', label: 'Icon', kind: 'social-icon' },
    { key: 'sortOrder', label: 'Sort order', kind: 'number' },
  ],
  columns: ['', 'Platform', 'URL', 'Sort'],
  row: (s) => [
    socialMark(resolveSocialIcon(s), s.platform),
    s.platform,
    el('span', 'text-text-dim text-xs break-all', {}, s.url),
    String(s.sortOrder ?? 0),
  ],
  api: adminApi.social as Resource<AdminSocial>['api'],
};

const GUIDES: Resource<AdminGuide> = {
  title: 'Guides',
  empty: 'No guides yet.',
  fields: [
    { key: 'title', label: 'Title', kind: 'text' },
    { key: 'summary', label: 'Summary (shown on the card)', kind: 'textarea', rows: 2 },
    { key: 'content', label: 'Body', kind: 'textarea', rows: 10 },
    { key: 'category', label: 'Category', kind: 'text' },
    { key: 'sortOrder', label: 'Sort order', kind: 'number' },
    { key: 'imageUrl', label: 'Cover image', kind: 'image' },
    { key: 'published', label: 'Published', kind: 'bool' },
  ],
  columns: ['Title', 'Category', 'Sort', 'Published'],
  row: (g) => [
    g.title,
    el('span', 'text-text-dim text-xs', {}, g.category),
    String(g.sortOrder ?? 0),
    g.published ? pill('live', 'ok') : pill('draft', 'warn'),
  ],
  api: adminApi.guides as Resource<AdminGuide>['api'],
};

const FOOTER_SECTIONS: Resource<AdminFooterSection> = {
  title: 'Footer columns',
  empty: 'No footer columns yet.',
  fields: [
    { key: 'title', label: 'Column title', kind: 'text' },
    { key: 'sortOrder', label: 'Sort order', kind: 'number' },
    { key: 'active', label: 'Visible', kind: 'bool' },
  ],
  columns: ['Column', 'Sort', 'Visible'],
  row: (f) => [f.title, String(f.sortOrder ?? 0), f.active ? pill('shown', 'ok') : pill('hidden', 'warn')],
  defaults: { active: true },
  api: adminApi.footerSections as Resource<AdminFooterSection>['api'],
};

/**
 * Links are edited flat, keyed by a column picker, rather than nested inside the
 * column editor — that keeps the one generic CRUD loop instead of a bespoke
 * nested widget. The list caches column titles so a link's row can name its
 * column instead of showing a cuid.
 */
const sectionTitles = new Map<string, string>();

const FOOTER_LINKS: Resource<AdminFooterLink> = {
  title: 'Footer links',
  empty: 'No footer links yet. Create a column first.',
  fields: [
    {
      key: 'sectionId',
      label: 'Column',
      kind: 'select',
      options: async () =>
        (await adminApi.footerSections.list()).map((s) => ({ value: s.id, label: s.title })),
    },
    { key: 'label', label: 'Label', kind: 'text' },
    { key: 'url', label: 'URL', kind: 'url' },
    { key: 'sortOrder', label: 'Sort order', kind: 'number' },
    { key: 'active', label: 'Visible', kind: 'bool' },
  ],
  columns: ['Column', 'Label', 'URL', 'Sort'],
  row: (l) => [
    el('span', 'text-text-dim text-xs', {}, sectionTitles.get(l.sectionId) ?? '—'),
    l.label,
    el('span', 'text-text-dim text-xs break-all', {}, l.url),
    String(l.sortOrder ?? 0),
  ],
  defaults: { active: true },
  api: {
    // Pull the columns alongside the links so `row` can resolve their titles.
    list: async () => {
      const [sections, links] = await Promise.all([
        adminApi.footerSections.list(),
        adminApi.footerLinks.list(),
      ]);
      sectionTitles.clear();
      for (const s of sections) sectionTitles.set(s.id, s.title);
      return links;
    },
    create: adminApi.footerLinks.create,
    update: adminApi.footerLinks.update,
    remove: adminApi.footerLinks.remove,
  } as Resource<AdminFooterLink>['api'],
};

export function renderCms(host: HTMLElement): () => void {
  const mount = <T extends { id: string }>(res: Resource<T>) => {
    const box = el('div');
    host.appendChild(box);
    return crudSection(box, res);
  };
  const stops = [
    mount(NEWS), mount(GUIDES), mount(FAQ), mount(LEGAL),
    mount(SOCIAL), mount(FOOTER_SECTIONS), mount(FOOTER_LINKS),
  ];
  const paymentsBox = el('div');
  host.appendChild(paymentsBox);
  stops.push(paymentMethodsSection(paymentsBox));
  host.className = 'flex flex-col gap-4';
  return () => stops.forEach((s) => s());
}

/**
 * The accepted-payment badges shown in both footers.
 *
 * Not a CRUD resource: the 88 icons ship with the app and the only editable
 * thing about one is whether it is shown, so this is a grid of switches over a
 * single settings row rather than a table of records. Each switch saves on the
 * spot — an operator turning on four card brands should not then have to find a
 * Save button.
 */
function paymentMethodsSection(host: HTMLElement): () => void {
  let cancelled = false;
  const listHost = el('div', 'min-h-[60px]');
  const search = input('Filter by name…');
  let all: AdminPaymentMethod[] = [];

  search.addEventListener('input', () => draw());

  async function load() {
    clear(listHost);
    listHost.appendChild(spinner());
    try {
      all = await adminApi.paymentMethods();
      if (cancelled) return;
      draw();
    } catch (e) {
      if (cancelled) return;
      clear(listHost);
      listHost.appendChild(errorBox(apiMessage(e, "Couldn't load payment methods."), () => void load()));
    }
  }

  function draw() {
    const term = search.value.trim().toLowerCase();
    const shown = term ? all.filter((m) => m.name.toLowerCase().includes(term) || m.slug.includes(term)) : all;
    const on = all.filter((m) => m.enabled).length;

    clear(listHost);
    listHost.appendChild(el('div', 'text-xs text-text-dim mb-3', {},
      on
        ? `${on} of ${all.length} methods are shown in the footer.`
        : 'No methods are switched on — the footer shows no payment badges at all.'));

    if (!shown.length) { listHost.appendChild(emptyBox('No method matches that filter.')); return; }

    const g = el('div', 'grid gap-2', { style: 'grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));' });
    for (const m of shown) {
      const c = checkbox(m.name, m.enabled);
      const icon = el('img', 'h-5 w-auto rounded bg-white/90 p-0.5 shrink-0',
        { src: `/payment-methods/${m.icon}.svg`, alt: '', loading: 'lazy' }) as HTMLImageElement;
      icon.addEventListener('error', () => icon.remove());
      c.input.addEventListener('change', () => void toggle(m, c.input));
      g.appendChild(el('div', 'flex items-center gap-2 min-w-0 rounded border border-border/60 px-2 py-1.5', {},
        icon, c.wrap));
    }
    listHost.appendChild(g);
  }

  async function toggle(m: AdminPaymentMethod, box: HTMLInputElement) {
    const next = box.checked;
    box.disabled = true;
    try {
      await adminApi.togglePaymentMethod(m.slug, next);
      m.enabled = next;
      draw();
    } catch (e) {
      // Put the switch back where it was: leaving it flipped would claim a save
      // that never happened.
      box.checked = !next;
      toastError(e, `Could not turn ${m.name} ${next ? 'on' : 'off'}`);
    } finally {
      box.disabled = false;
    }
  }

  host.appendChild(panel('Accepted payment methods',
    el('div', 'text-xs text-text-dim mb-3', {},
      'These badges appear in the site footer and in the game footer. Switching one on is a '
      + 'claim that the method is accepted at checkout — turn on only what a provider actually settles.'),
    el('div', 'mb-3', {}, search),
    listHost));
  void load();
  return () => { cancelled = true; };
}

/**
 * The body of a save: ONLY the keys this resource declares as fields.
 *
 * The editor seeds its values from the record it is editing, so sending them
 * back wholesale also sent `id`, `createdAt`, `updatedAt` and the server-owned
 * `slug`. The API validates with `forbidNonWhitelisted`, which answers 400
 * "property id should not exist" — so EVERY edit of an existing entry failed,
 * including ticking Published on a news post or a legal page. Creates worked,
 * which is why the panel looked half-alive rather than broken.
 */
export function editablePayload<T extends { id: string }>(
  res: Pick<Resource<T>, 'fields'>,
  values: Record<string, unknown>,
): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const f of res.fields) if (f.key in values) out[f.key] = values[f.key];
  return out;
}

function crudSection<T extends { id: string }>(host: HTMLElement, res: Resource<T>): () => void {
  let cancelled = false;
  const listHost = el('div', 'min-h-[60px]');

  async function load() {
    clear(listHost);
    listHost.appendChild(spinner());
    try {
      const items = await res.api.list();
      if (cancelled) return;
      clear(listHost);
      if (!items?.length) { listHost.appendChild(emptyBox(res.empty)); return; }
      listHost.appendChild(table([...res.columns, ''], items.map((it) => [
        ...res.row(it),
        el('div', 'flex gap-1.5', {},
          btn('Edit', () => openEditor(it)),
          btn('Delete', () => void remove(it), 'danger')),
      ])));
    } catch (e) {
      if (cancelled) return;
      clear(listHost);
      listHost.appendChild(errorBox(apiMessage(e, `Couldn't load ${res.title.toLowerCase()}.`), () => void load()));
    }
  }

  async function remove(item: T) {
    const ok = await confirmDialog(`Delete from ${res.title}`, 'This cannot be undone. Delete this entry?', 'Delete');
    if (!ok) return;
    try {
      await res.api.remove(item.id);
      toast('Deleted');
      void load();
    } catch (e) { toastError(e, 'Could not delete'); }
  }

  function openEditor(item?: T) {
    const values: Record<string, unknown> = item ? { ...item } : { ...(res.defaults ?? {}) };
    const body = el('div', 'flex flex-col gap-3');

    for (const f of res.fields) {
      if (f.kind === 'bool') {
        const c = checkbox(f.label, Boolean(values[f.key]));
        c.input.addEventListener('change', () => { values[f.key] = c.input.checked; });
        body.appendChild(c.wrap);
        continue;
      }
      if (f.kind === 'image') {
        const url = input('https://… or upload a file', String(values[f.key] ?? ''));
        url.addEventListener('input', () => { values[f.key] = url.value.trim(); });
        const file = el('input', 'text-xs text-text-dim', { type: 'file', accept: 'image/*' });
        file.addEventListener('change', () => {
          const picked = file.files?.[0];
          if (!picked) return;
          if (picked.size > 2_000_000) { toast('Image must be under 2 MB', 'error'); file.value = ''; return; }
          const reader = new FileReader();
          reader.onload = () => { url.value = String(reader.result ?? ''); values[f.key] = url.value; };
          reader.onerror = () => toast('Could not read that image', 'error');
          reader.readAsDataURL(picked);
        });
        body.appendChild(el('div', '', {}, labelled(f.label, url), el('div', 'mt-1.5', {}, file)));
        continue;
      }
      if (f.kind === 'social-icon') {
        const chosen = String(values[f.key] ?? '');
        body.appendChild(labelled(f.label, socialPicker(chosen, (slug) => { values[f.key] = slug; })));
        continue;
      }
      if (f.kind === 'select') {
        const sel = el('select',
          'w-full rounded bg-surface border border-border px-2 py-1.5 text-sm') as HTMLSelectElement;
        sel.appendChild(el('option', '', { value: '' }, 'Loading…'));
        sel.addEventListener('change', () => { values[f.key] = sel.value; });
        void f.options().then((opts) => {
          clear(sel);
          sel.appendChild(el('option', '', { value: '' }, '— choose —'));
          for (const o of opts) sel.appendChild(el('option', '', { value: o.value }, o.label));
          // Re-apply the current value only once the options exist, otherwise the
          // assignment is dropped and editing a link would silently reset it.
          sel.value = String(values[f.key] ?? '');
        }).catch(() => toast('Could not load the list of options', 'error'));
        body.appendChild(labelled(f.label, sel));
        continue;
      }
      if (f.kind === 'date') {
        // A `date` input only accepts YYYY-MM-DD, so an ISO stamp from the API
        // has to be cut down or the box renders empty and the save would then
        // clear a date nobody touched.
        const day = String(values[f.key] ?? '').slice(0, 10);
        const inp = input(f.label, day, 'date');
        inp.addEventListener('input', () => { values[f.key] = inp.value; });
        values[f.key] = day;
        body.appendChild(labelled(f.label, inp));
        if (f.hint) body.appendChild(el('div', 'text-[11px] text-text-dim -mt-2', {}, f.hint));
        continue;
      }
      if (f.kind === 'textarea') {
        const ta = textarea(f.label, String(values[f.key] ?? ''), f.rows ?? 5);
        ta.addEventListener('input', () => { values[f.key] = ta.value; });
        body.appendChild(labelled(f.label, ta));
        continue;
      }
      const inp = input(f.label, values[f.key] == null ? '' : String(values[f.key]), f.kind === 'number' ? 'number' : 'text');
      inp.addEventListener('input', () => {
        values[f.key] = f.kind === 'number' ? Number(inp.value || 0) : inp.value;
      });
      body.appendChild(labelled(f.label, inp));
    }

    const save = btn(item ? 'Save changes' : 'Create', async () => {
      const missing = res.fields.find((f) => f.kind !== 'bool' && f.kind !== 'image' && f.kind !== 'number'
        && f.kind !== 'social-icon' && f.kind !== 'date'
        && !String(values[f.key] ?? '').trim());
      if (missing) { toast(`${missing.label} is required`, 'error'); return; }
      try {
        const payload = editablePayload(res, values);
        if (item) await res.api.update(item.id, payload as Partial<T>);
        else await res.api.create(payload as Partial<T>);
        toast(item ? 'Saved' : 'Created');
        close();
        void load();
      } catch (e) { toastError(e, 'Could not save'); }
    }, 'primary');
    const close = modal(item ? `Edit — ${res.title}` : `New — ${res.title}`, body, [btn('Cancel', () => close()), save]);
  }

  host.appendChild(panel(res.title,
    el('div', 'flex justify-end mb-3', {}, btn(`New ${res.title.replace(/s$/, '').toLowerCase()}`, () => openEditor(), 'primary')),
    listHost));
  void load();
  return () => { cancelled = true; };
}


// ─── Social marks ────────────────────────────────────────────────────────────

/** One mark, or the platform's initial when no icon resolves. */
function socialMark(src: string | null, platform: string): HTMLElement {
  if (!src) {
    return el('span',
      'inline-flex items-center justify-center w-6 h-6 rounded bg-surface-2 text-[10px] text-text-dim uppercase',
      {}, (platform || '?').slice(0, 1));
  }
  // Masked rather than <img> so the mark takes the row's text colour — see
  // socialMaskStyle for why an <img> cannot.
  const mark = el('span', 'w-6 h-6 inline-block align-middle');
  mark.setAttribute('style', socialMaskStyle(src));
  return mark;
}

/**
 * The icon chooser: every shipped mark as a clickable tile, plus a "none" tile.
 *
 * A grid rather than a dropdown because the thing being chosen IS a picture —
 * an operator picking "instagram" off a text list is reading the word and hoping,
 * and the wrong mark in a footer is the kind of mistake nobody reports.
 */
function socialPicker(initial: string, onPick: (slug: string) => void): HTMLElement {
  let current = (initial || '').trim().toLowerCase();
  const grid = el('div', 'grid gap-1.5 mt-1',
    { style: 'grid-template-columns: repeat(auto-fill, minmax(56px, 1fr));' });
  const tiles = new Map<string, HTMLElement>();

  const paint = () => {
    for (const [slug, tile] of tiles) {
      const on = slug === current;
      tile.className = 'flex flex-col items-center gap-1 rounded border p-1.5 cursor-pointer transition '
        + (on ? 'border-gold bg-[#322820] text-gold' : 'border-border text-text-dim hover:border-gold/60');
    }
  };

  const tile = (slug: string, label: string, node: Node) => {
    const t = el('div', '', { title: label, onClick: () => { current = slug; onPick(slug); paint(); } },
      node, el('span', 'text-[9px] leading-none truncate max-w-full', {}, label));
    tiles.set(slug, t);
    grid.appendChild(t);
  };

  tile('', 'None', el('span', 'w-6 h-6 inline-flex items-center justify-center text-sm', {}, '—'));
  for (const icon of SOCIAL_ICONS) {
    const mark = el('span', 'w-6 h-6 inline-block');
    mark.setAttribute('style', socialMaskStyle(`/social/${icon.slug}.svg`));
    tile(icon.slug, icon.label, mark);
  }
  paint();
  return grid;
}
